import { getMonthToDateRange, type DatePeriod } from '@/utils/date';
import {
  parseActiveStatusFilter,
  parseNumberFilter,
  parseTextFilter,
  toActiveStatusFilterText,
  toFilterText,
  toGeoFilterValues,
  toGeoSearch,
  toPeriodFilterValues,
  toPeriodSearch,
} from '@/utils/filters';
import type {
  DcrSheetSearch,
  DistributionMemberParams,
  SpecimenDistributionSearch,
  SpecimenDrillSearch,
  SpecimenZmsSearch,
  SpecimenInstitutionSearch,
  SpecimenListSearch,
} from '../types/product.types';

type FilterValues = Record<string, string>;

type ProductReportSearch = Pick<
  SpecimenInstitutionSearch,
  | 'dateFrom'
  | 'dateTo'
  | 'userId'
  | 'divisionId'
  | 'districtId'
  | 'thanaId'
  | 'bookTypeNo'
  | 'bookEditionId'
  | 'companyId'
  | 'departmentId'
>;

type DistributionFilterSearch = ProductReportSearch &
  Pick<SpecimenDistributionSearch, 'approveTypeNo' | 'receiverMobile' | 'instituteCode'>;

type InstitutionFilterSearch = ProductReportSearch & Pick<SpecimenInstitutionSearch, 'specimenId'>;

function toProductReportFilterValues(search: ProductReportSearch): FilterValues {
  return {
    ...toPeriodFilterValues(search, getMonthToDateRange()),
    userId: toFilterText(search.userId),
    ...toGeoFilterValues(search),
    bookTypeNo: toFilterText(search.bookTypeNo),
    bookEditionId: toFilterText(search.bookEditionId),
    companyId: toFilterText(search.companyId),
    departmentId: toFilterText(search.departmentId),
  };
}

function toProductReportSearch(values: FilterValues): ProductReportSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    ...toGeoSearch(values),
    bookTypeNo: parseNumberFilter(values.bookTypeNo),
    bookEditionId: parseNumberFilter(values.bookEditionId),
    companyId: parseNumberFilter(values.companyId),
    departmentId: parseNumberFilter(values.departmentId),
  };
}

export function toDistributionFilterValues(search: DistributionFilterSearch): FilterValues {
  return {
    ...toProductReportFilterValues(search),
    receiverMobile: toFilterText(search.receiverMobile),
    instituteCode: toFilterText(search.instituteCode),
    approveTypeNo: toFilterText(search.approveTypeNo),
  };
}

export function toDistributionFilterSearch(values: FilterValues): DistributionFilterSearch {
  return {
    ...toProductReportSearch(values),
    receiverMobile: parseTextFilter(values.receiverMobile),
    instituteCode: parseNumberFilter(values.instituteCode),
    approveTypeNo: parseNumberFilter(values.approveTypeNo),
  };
}

export function toDistributionMemberParams({
  from: _from,
  ...memberParams
}: SpecimenDrillSearch): DistributionMemberParams {
  return memberParams;
}

export function toSpecimenDrillSearch({
  q: _q,
  ...drillSearch
}: SpecimenZmsSearch): SpecimenDrillSearch {
  return drillSearch;
}

export function toInstitutionFilterValues(search: InstitutionFilterSearch): FilterValues {
  return { ...toProductReportFilterValues(search), specimenId: toFilterText(search.specimenId) };
}

export function toInstitutionFilterSearch(values: FilterValues): InstitutionFilterSearch {
  return { ...toProductReportSearch(values), specimenId: parseNumberFilter(values.specimenId) };
}

type CatalogueFilterSearch = Pick<
  SpecimenListSearch,
  'isActive' | 'bookTypeNo' | 'bookEditionId' | 'insertedFrom' | 'insertedTo'
>;

export function toCatalogueFilterValues(search: CatalogueFilterSearch): FilterValues {
  return {
    isActive: toActiveStatusFilterText(search.isActive),
    bookTypeNo: toFilterText(search.bookTypeNo),
    bookEditionId: toFilterText(search.bookEditionId),
    insertedFrom: toFilterText(search.insertedFrom),
    insertedTo: toFilterText(search.insertedTo),
  };
}

export function toCatalogueFilterSearch(values: FilterValues): CatalogueFilterSearch {
  return {
    isActive: parseActiveStatusFilter(values.isActive),
    bookTypeNo: parseNumberFilter(values.bookTypeNo),
    bookEditionId: parseNumberFilter(values.bookEditionId),
    insertedFrom: parseTextFilter(values.insertedFrom),
    insertedTo: parseTextFilter(values.insertedTo),
  };
}

export function toSheetFilterValues(
  search: DcrSheetSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    userId: toFilterText(search.userId),
    ...toPeriodFilterValues(search, defaultPeriod),
    dcrTypeId: toFilterText(search.dcrTypeId),
  };
}

export function toSheetFilterSearch(values: FilterValues, isSingleDay: boolean): DcrSheetSearch {
  const period = toPeriodSearch(values);
  return {
    userId: parseNumberFilter(values.userId),
    dateFrom: period.dateFrom,
    dateTo: isSingleDay ? period.dateFrom : period.dateTo,
    dcrTypeId: parseNumberFilter(values.dcrTypeId),
  };
}
